import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";

import { useCreateActionPlan } from "../../../api/v2/createActionPlan";
import { ActionPlanFormData, actionPlanFormSchema, ScenarioOption } from "../types";
import { ActionPlanSummary } from "@mutiny-group/sbo-openapi-client";
import { AxiosError } from "axios";

type UseActionPlanFormProps = {
  scenarioOption?: ScenarioOption;
  onSuccess: (result: ActionPlanSummary) => void;
  onError: (error: AxiosError) => void;
};

export const useActionPlanForm = ({
  scenarioOption,
  onSuccess,
  onError,
}: UseActionPlanFormProps) => {
  const createActionPlanMutation = useCreateActionPlan({
    config: {
      onSuccess,
      onError,
    },
  });

  const form = useForm<ActionPlanFormData>({
    resolver: zodResolver(actionPlanFormSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      scenarioId: scenarioOption?.uuid,
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
  } = form;

  const watchedScenarioId = watch("scenarioId");

  // Initialize form when modal opens
  React.useEffect(() => {
    reset({
      name: "",
      scenarioId: scenarioOption?.uuid,
    });
  }, [reset, scenarioOption]);

  const onSubmit = React.useCallback(
    (data: ActionPlanFormData) => {
      createActionPlanMutation.mutate({
        name: data.name,
        scenarioId: data.scenarioId,
      });
    },
    [createActionPlanMutation]
  );

  return {
    control,
    handleSubmit,
    errors,
    isValid,
    watchedScenarioId,
    onSubmit,
    isSubmitting: createActionPlanMutation.isPending,
  };
};
